/**
 * i18n messages for bilingual report components (en/ar).
 * Registered in core/plugins/i18n.ts via deep merge.
 */
export const reportMessages = {
  en: {
    reports: {
      // Shared labels
      client: 'Client',
      clientBoardName: 'Client Board Name',
      startDate: 'Start Date',
      endDate: 'End Date',
      product: 'Product',
      year: 'Year',
      scholar: 'Scholar',
      designation: 'Designation',
      scholarsList: 'Scholars List',
      submit: 'Submit',
      add: 'Add',
      remove: 'Remove',
      save: 'Save',

      // Risk ratings (used in dropdowns and badge display)
      riskRatings: {
        high: 'High',
        moderate: 'Moderate',
        low: 'Low',
      },

      // Scholar designations
      roles: {
        srb: 'Shariyah Review Bureau',
        chairman: 'Chairman of the committee',
        member: 'Member of the committee',
      },

      // ResponseForm
      responseForm: {
        responsesSubmitted: 'Responses Submitted',
        responsesCollected: 'The responses have been collected.',
        reportClosed: 'The report is closed.',
        thankYou: 'Thank You',
        observation: 'Observation',
        riskRating: 'Risk Rating:',
        impact: 'Impact',
        recommendation: 'Recommendation',
        examplesOfTransactions: 'Examples of transactions in which the observation occurred:',
        references: 'References',
        rootCause: 'Root Cause',
        managementResponse: 'Management Response',
        responsibility: 'Responsibility',
        targetDate: 'Target Date',
      },

      // QualifiedReport
      qualifiedReport: {
        itemName: 'Item Name',
        itemReceived: 'Item Recieved',
        removeItem: 'Remove Item',
        addItem: 'Add Item',
        receiptStatus: {
          none: 'None',
          partial: 'Partial',
          all: 'All',
        },
        breadcrumbNew: 'New Qualified Report',
        breadcrumbEdit: 'Edit Qualified Report',
      },

      // SSBReport
      ssbReport: {
        ssbCompliance: 'SSB Compliance',
        complianceText: 'Compliance Text',
        reason: 'Reason',
        addReason: 'Add Reason',
        removeReason: 'Remove Reason',
        additionalOpinions: 'Additional Opinions',
        opinion: 'Opinion',
        addOpinion: 'Add Opinion',
        removeOpinion: 'Remove Opinion',
        noAdditionalOpinions: 'No Additional Opinions',
        breadcrumbEdit: 'Edit SSB Report',
        // Dynamic compliance text templates use {endDate} placeholder
        complianceTextTemplate:
          "Based on our (i) oversight and supervision of the Company's affairs related to Shari'a compliance, and (ii) a period-specific review of the activities performed, we report that in our opinion, the Company's financial arrangements, contracts and transactions with its customers, other financial companies and stakeholders for the year ended {endDate}  are in compliance with Shari'a standards and guidelines approved by us.",
        nonComplianceTextTemplate:
          "Based on our (i) oversight and supervision of the Company's affairs related to Sharīʻah compliance, and (ii) a period-specific review of the activities performed, we report that in our opinion we cannot confirm that the Company's financial arrangements, contracts and transactions with its customers, other financial companies and stakeholders for the year ended {endDate} are in compliance with Sharīʻah standards and guidelines approved by us due to the following reason(s):",
      },

      // AuditReport
      auditReport: {
        breadcrumbNew: 'New Audit Report',
        breadcrumbEdit: 'Edit Audit Report',
      },
    },
  },
  ar: {
    reports: {
      client: 'العميل',
      clientBoardName: 'اسم مجلس إدارة العميل',
      startDate: 'تاريخ البدء',
      endDate: 'تاريخ الانتهاء',
      product: 'المنتج',
      year: 'السنة',
      scholar: 'العالم الشرعي',
      designation: 'المسمى الوظيفي',
      scholarsList: 'قائمة العلماء',
      submit: 'إرسال',
      add: 'إضافة',
      remove: 'حذف',
      save: 'حفظ',

      riskRatings: {
        high: 'عالي',
        moderate: 'متوسط',
        low: 'منخفض',
      },

      roles: {
        srb: 'دار المراجعة الشرعية',
        chairman: 'رئيس لجنة الرقابة الشرعية',
        member: 'عضو لجنة الرقابة الشرعية',
      },

      responseForm: {
        responsesSubmitted: 'تم إرسال الردود',
        responsesCollected: 'تم جمع الردود.',
        reportClosed: 'التقرير مغلق.',
        thankYou: 'شكراً لكم',
        observation: 'الملاحظة',
        riskRating: 'التقييم :',
        impact: 'الأثر',
        recommendation: 'التوصية',
        examplesOfTransactions: 'أمثلة على المعاملات التي وقعت فيها الملاحظة :',
        references: 'المرجع',
        rootCause: 'المسبب',
        managementResponse: 'رد الإدارة',
        responsibility: 'المسؤول',
        targetDate: 'تاريخ تنفيذ الإجراء',
      },

      qualifiedReport: {
        itemName: 'اسم العنصر',
        itemReceived: 'تم الاستلام',
        removeItem: 'حذف عنصر',
        addItem: 'إضافة عنصر',
        receiptStatus: {
          none: 'لم يتم استلام شيء',
          partial: 'جزئية',
          all: 'كاملة',
        },
        breadcrumbNew: 'تقرير مؤهل جديد',
        breadcrumbEdit: 'تعديل التقرير المؤهل',
      },

      ssbReport: {
        ssbCompliance: 'التوافق مع الهيئة الشرعية',
        complianceText: 'نص التوافق',
        reason: 'السبب',
        addReason: 'إضافة سبب',
        removeReason: 'حذف سبب',
        additionalOpinions: 'آراء إضافية',
        opinion: 'الرأي',
        addOpinion: 'إضافة رأي',
        removeOpinion: 'حذف رأي',
        noAdditionalOpinions: 'لا توجد آراء إضافية',
        breadcrumbEdit: 'تعديل تقرير الهيئة الشرعية',
        complianceTextTemplate:
          'هذا التقرير وما يتضمنه من رأي مستند على إشرافنا على أعمال الشركة المتعلقة بالالتزام بالضوابط والمعايير الشرعية أولًا، ومراجعة عملياتها وأنشطتها للفترة المحددة ثانيًا، وعليه فإننا نؤكد على أن الترتيبات المالية الخاصة بالشركة والعقود والمعاملات المنفذة مع عملائها والأطراف الأخرى التي أُجريت في السنة المالية المنتهية في {endDate} قد تمت وفق الضوابط والمعايير الشرعية المعتمدة.',
        nonComplianceTextTemplate:
          'هذا التقرير وما يتضمنه من رأي مستند على إشرافنا على أعمال الشركة المتعلقة بالالتزام بالضوابط والمعايير الشرعية أولًا، ومراجعة عملياتها وأنشطتها للفترة المحددة ثانيًا، وعليه فإنه لا يمكننا التأكيد على أن الترتيبات المالية الخاصة بالشركة والعقود والمعاملات المنفذة مع عملائها والأطراف الأخرى التي أُجريت في السنة المالية المنتهية في {endDate} قد تمت وفق الضوابط والمعايير الشرعية المعتمدة للأسباب التالية:',
      },

      auditReport: {
        breadcrumbNew: 'تقرير تدقيق جديد',
        breadcrumbEdit: 'تعديل تقرير التدقيق',
      },
    },
  },
}
